import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ApiClient,
  type ApiResult,
  type CommandIntent,
  errorMessageFa,
  isApiError,
} from '@saay/api-client';
import {
  applyFieldErrors,
  AlertDialog,
  Button,
  type ButtonVariant,
  Dialog,
  FieldErrorList,
} from '../kit.js';
import { type ApprovalPending, type CommandOutcome, useCommand } from '@saay/web-app';
import { type ReactNode, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { reasonSchema } from '../validation.js';
import { AdminCommandFeedback, useAdminFeedback } from './admin-feedback.js';
import { ReasonField } from './reason-field.js';
import { useSubmitBlocked } from './form.js';

/**
 * A command whose only input is a reason (AC §18.3: required, trimmed, at most 1000; or optional where
 * the contract says so): status transitions, branch decisions, payout decisions, approvals of change
 * requests. Short confirms use a centered alert (XAP-06); success is shown only after the server answered.
 */
export function ReasonDialog<T>({
  children,
  description,
  eyebrow,
  guidance,
  name,
  onDone,
  onError,
  onOpenChange,
  reasonLabel = 'دلیل',
  required = true,
  send,
  submitLabel,
  title,
  variant = 'primary',
}: {
  /** Extra content above the reason (for example the facts the decision is about). */
  readonly children?: ReactNode;
  readonly description?: ReactNode;
  readonly eyebrow?: ReactNode;
  /** The recovery line under the server message, chosen by error code. */
  readonly guidance?: (error: unknown) => ReactNode;
  readonly name: string;
  /** After a server-confirmed answer (2xx or `202 ApprovalPending`). */
  readonly onDone: (
    result: ApiResult<T>,
    approval: ApprovalPending | undefined,
  ) => void | Promise<void>;
  /** After an envelope error (for example to reload the resource on a conflict). */
  readonly onError?: (error: unknown) => void | Promise<void>;
  readonly onOpenChange: (open: boolean) => void;
  readonly reasonLabel?: string;
  readonly required?: boolean;
  readonly send: (
    client: ApiClient,
    reason: string,
    intent: CommandIntent,
  ) => Promise<ApiResult<T>>;
  readonly submitLabel: string;
  readonly title: ReactNode;
  readonly variant?: ButtonVariant;
}) {
  const feedback = useAdminFeedback();
  const offline = useSubmitBlocked();
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const form = useForm<{ reason: string }>({
    defaultValues: { reason: '' },
    resolver: zodResolver(z.object({ reason: reasonSchema(required) })),
  });
  const command = useCommand<string, T>(name, send, {
    onApprovalPending: async (approval, result) => {
      feedback.approvalSubmitted(approval.approvalRequestId);
      onOpenChange(false);
      await onDone(result as unknown as ApiResult<T>, approval);
    },
    onSuccess: async (result) => {
      await onDone(result, undefined);
      onOpenChange(false);
    },
  });

  async function handle(outcome: CommandOutcome<T>): Promise<void> {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    setUnmapped(applyFieldErrors(outcome.error, ['reason'], form.setError));
    await onError?.(outcome.error);
  }

  const error = command.state.status === 'error' ? command.state.error : undefined;
  const line = error === undefined ? undefined : guidance?.(error);
  const pending = command.state.status === 'pending';

  async function submit(): Promise<void> {
    if (offline.blocked || pending) {
      return;
    }
    setUnmapped([]);
    await form.handleSubmit(async (values) => {
      await handle(await command.submit(values.reason.trim()));
    })();
  }

  if (command.state.status === 'approval-pending') {
    return (
      <Dialog
        {...(eyebrow === undefined ? {} : { eyebrow })}
        footer={
          <Button onClick={() => onOpenChange(false)} type="button" variant="secondary">
            بستن
          </Button>
        }
        onOpenChange={(open) => {
          if (!open) command.reset();
          onOpenChange(open);
        }}
        open
        size="small"
        title={title}
      >
        <AdminCommandFeedback
          onCheckStatus={() => void command.checkStatus().then(handle)}
          state={command.state}
        />
      </Dialog>
    );
  }

  return (
    <AlertDialog
      cancelLabel="انصراف"
      confirmLabel={submitLabel}
      destructive={variant === 'danger'}
      keepOpenOnConfirm
      description={
        <div className="admin-reason-alert">
          {eyebrow === undefined ? null : <p className="saay-eyebrow">{eyebrow}</p>}
          {description === undefined ? null : <p>{description}</p>}
          {children}
          <form
            className="admin-form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <ReasonField
              error={form.formState.errors.reason?.message}
              intent={name}
              label={reasonLabel}
              optional={!required}
              onChange={(value) =>
                form.setValue('reason', value, { shouldDirty: true, shouldValidate: true })
              }
              value={form.watch('reason')}
            />
            <FieldErrorList errors={unmapped} />
            {/* A refusal with its own recovery line shows that line only, not the same refusal twice. */}
            {line === undefined || line === null ? (
              <AdminCommandFeedback
                onCheckStatus={() => void command.checkStatus().then(handle)}
                state={command.state}
              />
            ) : null}
            {line === undefined || line === null ? null : (
              <div className="saay-banner" data-tone="warning" role="alert">
                <div>
                  <p>{errorMessageFa(error)}</p>
                  <div>{line}</div>
                </div>
              </div>
            )}
          </form>
        </div>
      }
      onConfirm={() => void submit()}
      onOpenChange={(open) => {
        if (!open) command.reset();
        onOpenChange(open);
      }}
      open
      title={title}
    />
  );
}
